import React, { useState, useEffect, useId } from "react";
import Layout from "@components/layout";
import SEO from "@components/seo";
import * as c from "./tour.module.scss";
import classNames from "classnames";
import { Script } from "gatsby";
import { Button } from "@components/shared";
import { useWistiaPlayer } from "@hooks/useWistiaPlayer";
import {
  haircutsImg,
  skincareImg,
  makeupImg,
  barberingImg,
  hairRemovalImg,
  massagesImg,
  tattoosImg,
  nailsImg,
  injectablesImg,
  spasImg,
  wellnessImg,
  otherImg,
} from "@images/flows/services";
import tourPreviewImg from "@images/tour/tour-view-blur.png";
import StaticGradient from "@components/shared/staticGradient";

const OPTIONS = [
  { label: "Hair Salon", value: "Hair Salon", img: haircutsImg, videoId: "1i004c236v" },
  { label: "Barbershop", value: "Barbershop", img: barberingImg, videoId: "1i004c236v" },
  { label: "Spa", value: "Spa", img: spasImg, videoId: "ih9en6iysa" },
  { label: "Beauty Studio", value: "Beauty Studio", img: makeupImg, videoId: "ih9en6iysa" },
  { label: "Skincare Studio", value: "Skincare Studio", img: skincareImg, videoId: "ih9en6iysa" },
  { label: "Hair Removal", value: "Hair Removal Studio", img: hairRemovalImg, videoId: "ih9en6iysa" },
  { label: "Nail Salon", value: "Nail Salon", img: nailsImg, videoId: "1i004c236v" },
  { label: "Massage Studio", value: "Massage Studio", img: massagesImg, videoId: "ih9en6iysa" },
  { label: "Tattoo & Piercing", value: "Tattoo & Piercing Studio", img: tattoosImg, videoId: "ih9en6iysa" },
  { label: "Med Spa", value: "Med Spa", img: injectablesImg, videoId: "0trz1kpypp" },
  { label: "Wellness Center", value: "Wellness Center", img: wellnessImg, videoId: "0trz1kpypp" },
  { label: "Other", value: "Other", img: otherImg, videoId: "ih9en6iysa" },
];

const CTA_OPTIONS = [
  {
    title: "Schedule a demo with us",
    description: "Set up a one-on-one demo with a team member. We'll answer your questions on a screenshare.",
    buttonText: "Book a demo",
    buttonLink: "/demo/",
    isMobileOnly: false,
  },
  {
    title: "Start a free 21-day trial",
    description: "Create your account within minutes and try our features with sample data. No credit card required.",
    buttonText: "Try it now",
    buttonLink: "/trial/",
    isMobileOnly: false,
  },
  {
    title: "Ready to try it for yourself or still have questions?",
    description:
      "Get your questions answered in a one-on-one demo call or try it on your own with sample data, no credit card required.",
    buttonText: "Book a demo",
    buttonLink: "/demo/",
    isMobileOnly: true,
    secondaryButton: { text: "Try it now", link: "/trial/" },
  },
];

export default function Tour() {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [playVideo, setPlayVideo] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isOverlayFading, setIsOverlayFading] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const wistiaContainerId = useId();

  useWistiaPlayer({
    wistiaContainerId,
    trackedEventName: selectedVideo ? `Tour Video - ${selectedVideo.value}` : "",
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 992);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
      setPlayVideo(false);
      setIsOverlayFading(false);
    };
  }, []);

  const handleVideoSelect = (option) => {
    setSelectedVideo(option);
    if (isMobile) startVideoWithFade();
  };

  const startVideoWithFade = () => {
    setIsVideoLoaded(true);
    setTimeout(() => {
      setIsOverlayFading(true);

      setTimeout(() => {
        setPlayVideo(true);
      }, 550);
      setTimeout(() => {
        setIsOverlayFading(false);
      }, 700);
    }, 300);
  };

  const handleWatchVideo = (e) => {
    if (e) e.preventDefault();
    startVideoWithFade();
  };

  return (
    <Layout>
      <Script src="https://fast.wistia.com/assets/external/E-v1.js" async strategy="idle" />
      <SEO
        title="Tour Mangomint"
        description="Watch a video tour of Mangomint's salon and spa booking and POS system. See why Mangomint is ranked the best management software for salons and spas."
      />
      <StaticGradient
        top="-200px"
        opacity="0.45"
        mask="linear-gradient(to bottom, #000 0%, transparent 100%)"
        height="400px"
      />
      <section className={c.container}>
        <div className={c.wrapper}>
          <div
            className={classNames(c.videoContainer, {
              [c.hideOverflow]: playVideo && isVideoLoaded,
            })}
          >
            {!playVideo && !isMobile && <img src={tourPreviewImg} alt="Tour preview" className={c.previewImage} />}

            {selectedVideo && (
              <div
                id="tour-video"
                className={classNames(c.videoContent, {
                  [c.visible]: playVideo && isVideoLoaded,
                })}
              >
                {isVideoLoaded && (
                  <div
                    id={wistiaContainerId}
                    className={classNames(
                      "wistia_embed",
                      `wistia_async_${selectedVideo.videoId}`,
                      "videoFoam=true",
                      "autoPlay=true"
                    )}
                  />
                )}
              </div>
            )}

            {(!playVideo || isOverlayFading) && (
              <div
                className={classNames(c.videoOverlay, {
                  [c.fadeOut]: isOverlayFading,
                })}
              >
                <div className={c.videoHeader}>
                  <h1 className="h3">Watch a video tour</h1>
                  <p className="p3">Which best describes your business?</p>
                </div>
                <div className={c.videoOptions}>
                  {OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      className={classNames(c.videoOption, { [c.selected]: selectedVideo?.value === option.value })}
                      onClick={() => handleVideoSelect(option)}
                    >
                      <img src={option.img} alt={`${option.label} icon`} />
                      <p>{option.label}</p>
                    </button>
                  ))}
                </div>

                <div className={c.watchVideoWrapper}>
                  <Button
                    onClick={handleWatchVideo}
                    className={classNames(c.watchVideoButton, {
                      [c.disabled]: !selectedVideo,
                      [c.enabled]: selectedVideo,
                    })}
                    disabled={!selectedVideo}
                  >
                    Watch video
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className={c.ctaContainer}>
            {CTA_OPTIONS.map((cta, index) => (
              <div key={index} className={classNames(c.card, { [c.mobileOnly]: cta.isMobileOnly })}>
                <h2 className="h5">{cta.title}</h2>
                <p className={classNames(c.cardDescription, "p3")}>{cta.description}</p>
                <div className={c.buttonGroup}>
                  <Button linkTo={cta.buttonLink}>{cta.buttonText}</Button>
                  {cta.secondaryButton && <Button linkTo={cta.secondaryButton.link}>{cta.secondaryButton.text}</Button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}

export function Head() {
  return <body className={c.body} />;
}
